import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import compact from 'lodash/compact';
import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import isBoolean from 'lodash/isBoolean';
import { format as currencyFormat } from 'currency-formatter';
import ModalPortal from '../ModalPortal';
import * as analytics from '../../services/analytics';
import logo from './chubb-logo-dark.svg';
import './Passport360EnrollmentPlansView.css';

const propTypes = {
  client: PropTypes.object,
  plans: PropTypes.array,
  plansFilter: PropTypes.object,
  onSubmit: PropTypes.func,
  updatePlansFilter: PropTypes.func,
};

const coverageDetailKeys = [
  'medical_evacuation_and_repatriation',
  'excess_medical_expense',
  'political_evacuation',
  'felonious_assault',
  'common_carrier_policyholder',
  'common_carrier_dependent_child',
  'common_carrier_spouse',
  'adandd_add_policyholder',
  'adandd_add_spouse',
  'adandd_dependent_child',
  'adandd_domestic_employee',
  'trip_delay_coverage',
  'baggage_delay_coverage',
  'travel_assistance_services',
  'trip_risk_intelligence_portal',
];

class Passport360EnrollmentPlansView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      modalTitle: null,
      modalBody: null,
    };
  }

  componentDidMount() {
    analytics.page('Passport 360 - Enrollment Plans');
  }

  handleNext = (e) => {
    this.props.updatePlansFilter({
      planName: e.target.dataset.planName,
    });
    this.props.onSubmit();
  };

  getCoverageDetailsValue(value) {
    if (isBoolean(value)) {
      return value ? 'Included' : 'Not Included';
    } else {
      return value || 'n/a';
    }
  }

  showInfoModal = (e) => {
    analytics.page('Passport 360 - Coverage Details Modal Opened', {
      title: e.currentTarget.dataset.modalTitle,
    });
    this.setState(
      {
        modalTitle: e.currentTarget.dataset.modalTitle,
        modalBody: e.currentTarget.dataset.modalBody,
      },
      () => {
        window.$(this.modal).modal('show');
      }
    );
  };

  trackDocClick = (e) => {
    analytics.track('Broker Portal - External Link Clicked', {
      label: e.target.dataset.key,
    });
  };

  get filteredPlans() {
    return filter(this.props.plans, {
      settings: {
        details: {
          plan_type: this.props.plansFilter.planType,
        },
      },
    });
  }

  get essentialPlan() {
    const plan = find(this.filteredPlans, {
      settings: {
        details: {
          plan_name: 'essential',
          optional_coverages: {
            golf_package: null,
            trip_cancellation: null,
          },
        },
      },
    });
    return plan;
  }

  get enhancedPlan() {
    return find(this.filteredPlans, {
      settings: {
        details: {
          plan_name: 'enhanced',
          optional_coverages: {
            golf_package: null,
            trip_cancellation: null,
          },
        },
      },
    });
  }

  get coverageDetailRows() {
    const essentialCoverageDetails = get(
      this.essentialPlan,
      'settings.details.coverage_details'
    );
    const enhancedCoverageDetails = get(
      this.enhancedPlan,
      'settings.details.coverage_details'
    );

    if (essentialCoverageDetails || enhancedCoverageDetails) {
      return compact(
        coverageDetailKeys.map((key) => {
          const essentialDetail = get(essentialCoverageDetails, key);
          const enhancedDetail = get(enhancedCoverageDetails, key);

          if (essentialDetail || enhancedDetail) {
            const title =
              get(essentialDetail, 'title') || get(enhancedDetail, 'title');
            const description =
              get(essentialDetail, 'description') ||
              get(enhancedDetail, 'description');
            return (
              <tr key={key}>
                <td>
                  {title}{' '}
                  <span
                    data-modal-title={title}
                    data-modal-body={description}
                    onClick={this.showInfoModal}
                  >
                    <i className="Passport360PolicyDetailsView-infoIcon fas fa-info-circle" />
                  </span>
                </td>
                {this.essentialPlan && (
                  <td>{this.getCoverageDetailsValue(essentialDetail.value)}</td>
                )}
                {this.enhancedPlan && (
                  <td>{this.getCoverageDetailsValue(enhancedDetail.value)}</td>
                )}
              </tr>
            );
          } else {
            return null;
          }
        })
      );
    } else {
      return null;
    }
  }

  get title() {
    const { actor, client } = this.props;
    if (actor === 'broker') {
      return `Which plan is right for ${client.person.first_name}?`;
    } else {
      return 'Which plan is right for you?';
    }
  }

  render() {
    return (
      <Fragment>
        <div className="row justify-content-center">
          <div className="col-md-12 p-t-30">
            <h3 className="text-center semi-bold">{this.title}</h3>
            <div className="Passport360EnrollmentPlansView-plans card m-t-20">
              <div
                className={classnames(
                  'Passport360EnrollmentPlansView-plansHeader',
                  {
                    'Passport360EnrollmentPlansView-plansHeader--singlePlan':
                      !this.essentialPlan || !this.enhancedPlan,
                  }
                )}
              >
                <div className="Passport360EnrollmentPlansView-plansHeader-col Passport360EnrollmentPlansView-plansHeader-underwriter">
                  <p className="text-uppercase semi-bold">
                    All policies issued by
                  </p>
                  <img src={logo} alt="Chubb logo" />
                </div>
                {this.essentialPlan && (
                  <div className="Passport360EnrollmentPlansView-plansHeader-col Passport360EnrollmentPlansView-plansHeader-essential">
                    <h4 className="Passport360EnrollmentPlansView-plansHeader-title">
                      {get(this.essentialPlan, 'settings.plan_title')}
                    </h4>
                    <p className="Passport360EnrollmentPlansView-plansHeader-price">
                      {currencyFormat(
                        get(this.essentialPlan, 'settings.details.plan_price'),
                        {
                          code: get(this.essentialPlan, 'currency'),
                          precision: 0,
                        }
                      )}
                    </p>
                    <button
                      className="btn btn-large btn-complete m-b-10"
                      data-plan-name="essential"
                      onClick={this.handleNext}
                    >
                      Select plan
                    </button>
                    <a
                      href={get(
                        this.essentialPlan,
                        'settings.sample_policy_url'
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="Passport360EnrollmentPlansView-plansHeader-policyDocLink"
                      data-key="essentialPlan"
                      onClick={this.trackDocClick}
                    >
                      <i className="Passport360EnrollmentPlansView-plansHeader-policyDocLink-icon fal fa-file-alt" />
                      view sample policy
                    </a>
                  </div>
                )}
                {this.enhancedPlan && (
                  <div className="Passport360EnrollmentPlansView-plansHeader-col Passport360EnrollmentPlansView-plansHeader-enhanced">
                    <h4 className="Passport360EnrollmentPlansView-plansHeader-title">
                      {get(this.enhancedPlan, 'settings.plan_title')}
                    </h4>
                    <p className="Passport360EnrollmentPlansView-plansHeader-price">
                      {currencyFormat(
                        get(this.enhancedPlan, 'settings.details.plan_price'),
                        {
                          code: get(this.enhancedPlan, 'currency'),
                          precision: 0,
                        }
                      )}
                    </p>
                    <button
                      className="btn btn-large btn-complete m-b-10"
                      data-plan-name="enhanced"
                      onClick={this.handleNext}
                    >
                      Select plan
                    </button>
                    <a
                      href={get(
                        this.enhancedPlan,
                        'settings.sample_policy_url'
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="Passport360EnrollmentPlansView-plansHeader-policyDocLink"
                      data-key="enhancedPlan"
                      onClick={this.trackDocClick}
                    >
                      <i className="Passport360EnrollmentPlansView-plansHeader-policyDocLink-icon fal fa-file-alt" />
                      view sample policy
                    </a>
                  </div>
                )}
              </div>
              <table className="Passport360EnrollmentPlansView-table table table-striped">
                <thead>
                  <tr>
                    <th>Benefits</th>
                    {this.essentialPlan && <th>Benefit amount</th>}
                    {this.enhancedPlan && <th>Benefit amount</th>}
                  </tr>
                </thead>
                <tbody>{this.coverageDetailRows}</tbody>
              </table>
            </div>
          </div>
        </div>

        <ModalPortal>
          <div className="modal" id="infoModal" ref={(c) => (this.modal = c)}>
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content-wrapper">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{this.state.modalTitle}</h5>
                    <button
                      type="button"
                      className="close"
                      data-dismiss="modal"
                      aria-label="Close"
                    >
                      <span aria-hidden="true">&times;</span>
                    </button>
                  </div>
                  <div className="modal-body">
                    <p>{this.state.modalBody}</p>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="submit"
                      className="btn btn-complete"
                      data-dismiss="modal"
                    >
                      Ok
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ModalPortal>
      </Fragment>
    );
  }
}

Passport360EnrollmentPlansView.propTypes = propTypes;

export default Passport360EnrollmentPlansView;
